import React, { useState } from "react";
import { Dialog, DialogProps } from "@mui/material";
import ConfirmationDialog from "./ConfirmationDialog";

interface EnhancedQueryDialogProps extends Omit<DialogProps, 'onClose'> {
  /**
   * Whether to show confirmation dialog before closing on ESC key
   * If false, ESC key will be completely disabled
   */
  showConfirmationOnEsc?: boolean;
  
  /**
   * Custom close handler - called when dialog should close
   */
  onClose?: () => void;
  
  /**
   * Whether there are unsaved changes that would be lost
   * Used to determine if confirmation is needed
   */
  hasUnsavedChanges?: boolean;
  
  /**
   * Custom confirmation dialog title
   */
  confirmationTitle?: string;
  
  /**
   * Custom confirmation dialog content
   */
  confirmationContent?: React.ReactNode;
}

/**
 * Enhanced Dialog component that prevents accidental closing via ESC key
 * for Query Editor components to prevent data loss
 */
const EnhancedQueryDialog: React.FC<EnhancedQueryDialogProps> = ({
  children,
  showConfirmationOnEsc = true,
  onClose,
  hasUnsavedChanges = false,
  confirmationTitle = "Unsaved Changes",
  confirmationContent = "You have unsaved changes that will be lost. Are you sure you want to close?",
  ...dialogProps
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleDialogClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    // Prevent closing on ESC key or backdrop click if there are unsaved changes
    if (reason === "escapeKeyDown") {
      if (hasUnsavedChanges && showConfirmationOnEsc) {
        // Show confirmation dialog
        setShowConfirmation(true);
      }
      // If no unsaved changes or confirmation disabled, do nothing (ESC disabled)
      return;
    }
    
    // Allow backdrop click to close (you can modify this behavior if needed)
    if (reason === "backdropClick" && hasUnsavedChanges && showConfirmationOnEsc) {
      setShowConfirmation(true);
      return;
    }
    
    // For other reasons, allow normal closing
    if (onClose) {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmation(false);
    if (onClose) {
      onClose();
    }
  };

  const handleCancelClose = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      <Dialog
        {...dialogProps}
        onClose={handleDialogClose}
        disableEscapeKeyDown={!showConfirmationOnEsc || hasUnsavedChanges}
      >
        {children}
      </Dialog>
      
      <ConfirmationDialog
        title={confirmationTitle}
        dialogContent={confirmationContent}
        openConfirmation={showConfirmation}
        handleCancel={handleCancelClose}
        handleConfirm={handleConfirmClose}
      />
    </>
  );
};

export default EnhancedQueryDialog;
